import { useMemo } from "react";
import {
  ArrowRight,
  Bell,
  Calculator,
  CheckCircle2,
  ClipboardList,
  Compass,
  ExternalLink,
  Lightbulb,
  Sparkles,
  Wallet,
} from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { useMe } from "@/hooks/useMe";
import { useSetupChecklist } from "@/hooks/useSetupChecklist";
import { useAdvisorCards } from "@/hooks/useAdvisor";
import { useCashAllocation, useStrategyAssumptions } from "@/hooks/useStrategy";
import { useBusinessProfile, useFamilyProfile } from "@/hooks/useProfile";
import { useTaxProjection } from "@/hooks/useTax";
import { useTaxPlays, type TaxPlay } from "@/hooks/useTaxPlays";
import { SankeyCard } from "@/components/dashboard/SankeyCard";
import { formatMoney, formatPercent } from "@/lib/utils";

// =============================================================================
// Dashboard — narrative, not dashboard-of-dashboards.
//
// One hero number (total tax this year), three ranked moves (the highest-dollar
// plays you haven't taken yet), one cash-flow Sankey, and a slim posture rail.
// Everything else (advisor cards, setup checklist details) lives one click away.
// =============================================================================

export function DashboardPage() {
  const me = useMe();
  const taxProjection = useTaxProjection();
  const cash = useCashAllocation();
  const business = useBusinessProfile();
  const family = useFamilyProfile();
  const assumptions = useStrategyAssumptions();
  const checklist = useSetupChecklist();
  const advisor = useAdvisorCards();
  const plays = useTaxPlays();

  const projection = taxProjection.data?.projection;
  const isBlocked = projection?.hasBlockingGaps === true;
  const blockingMissing = projection?.missingInputs ?? [];

  // Ranked top moves: only show APPLICABLE plays with a quantified impact, sorted descending.
  const topMoves = useMemo(() => {
    if (!plays.data) return [];
    return plays.data.plays
      .filter((p) => p.isApplicable && (p.estimatedDollarImpact ?? 0) > 0)
      .sort((a, b) => (b.estimatedDollarImpact ?? 0) - (a.estimatedDollarImpact ?? 0))
      .slice(0, 3);
  }, [plays.data]);

  const totalQuantifiedSavings = useMemo(
    () =>
      (plays.data?.plays ?? [])
        .filter((p) => p.isApplicable && p.estimatedDollarImpact != null)
        .reduce((sum, p) => sum + (p.estimatedDollarImpact ?? 0), 0),
    [plays.data?.plays],
  );

  // Critical advisor cards still get a slim banner — but only if Critical.
  const criticalAlert = (advisor.data ?? []).find((c) => c.severity === "Critical");

  const checklistTotal = checklist.data?.progress.total ?? 0;
  const checklistDone = checklist.data?.progress.completed ?? 0;
  const checklistPct =
    checklistTotal > 0 ? Math.round((checklistDone / checklistTotal) * 100) : 0;

  return (
    <div className="space-y-8">
      <FirstName name={me.data?.displayName ?? undefined} />

      {criticalAlert && <CriticalBanner card={criticalAlert} />}

      {/* === HERO ============================================================ */}
      {isBlocked ? (
        <BlockedHero missing={blockingMissing} />
      ) : (
        <TaxHero
          projection={projection}
          loading={taxProjection.isLoading}
        />
      )}

      {/* === NEXT MOVES ===================================================== */}
      <NextMoves
        moves={topMoves}
        totalPotential={totalQuantifiedSavings}
        loading={plays.isLoading}
        blocked={isBlocked}
      />

      {/* === MIDDLE: cash flow + posture rail =============================== */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <SankeyCard />
        </div>
        <div className="space-y-4">
          <PostureRail
            entity={business.data?.entityType}
            treatment={business.data?.taxTreatment}
            filing={family.data?.filingStatus ?? undefined}
            state={family.data?.stateOfResidence}
            kids={family.data?.children?.length ?? 0}
            hsaEligible={family.data?.isHsaEligible}
          />
          <CashPlanRail
            taxReserve={cash.data?.plan?.projectedAnnualTaxReserve}
            solo401k={cash.data?.plan?.projectedAnnualSolo401k}
            sweepPct={cash.data?.plan?.taxSavingsSweepPercent}
            assumptionsRoute={
              assumptions.data?.hourlyRate == null ? "/settings" : null
            }
            loading={cash.isLoading}
          />
          <SetupRail done={checklistDone} total={checklistTotal} pct={checklistPct} />
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// HERO variants
// =============================================================================

function TaxHero({
  projection,
  loading,
}: {
  projection?: ReturnType<typeof useTaxProjection>["data"] extends infer T
    ? T extends { projection: infer P }
      ? P
      : never
    : never;
  loading: boolean;
}) {
  if (loading || !projection) {
    return (
      <Card>
        <CardContent className="p-8">
          <Skeleton className="mb-3 h-4 w-32" />
          <Skeleton className="mb-2 h-12 w-72" />
          <Skeleton className="h-4 w-96" />
        </CardContent>
      </Card>
    );
  }

  const totalTax = projection.totalLiability;
  const fed = projection.totalFederalTax;
  const state = projection.totalStateTax;
  const withheld = projection.totalWithholdingYtd + projection.totalEstimatedPaidYtd;
  const remaining = projection.remainingLiabilityToCover;
  const effective = projection.effectiveTaxRateOnAgi;
  const marginal = projection.nextDollarBlendedRate;
  const takeHome = projection.agi - totalTax;

  return (
    <Card>
      <CardContent className="p-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="min-w-0">
            <div className="text-2xs font-medium text-muted-foreground">
              Tax year {projection.taxYear} · projected
            </div>
            <div className="mt-2 flex items-baseline gap-3">
              <div className="text-5xl font-bold tabular-nums tracking-tight">
                {formatMoney(totalTax)}
              </div>
              <div className="text-base text-muted-foreground">total tax</div>
            </div>
            <div className="mt-3 max-w-2xl text-sm text-muted-foreground">
              <span className="text-foreground">{formatMoney(fed)}</span> federal
              {" + "}
              <span className="text-foreground">{formatMoney(state)}</span>{" "}
              {projection.stateCode}
              {" · "}
              <span className="text-foreground">{formatPercent(effective, 1)}</span>{" "}
              effective rate
              {" · "}
              every additional dollar costs{" "}
              <span className="text-foreground">{formatPercent(marginal, 1)}</span>
            </div>
          </div>

          <div className="flex shrink-0 flex-col items-end gap-2 rounded-lg border border-border bg-surface/50 px-5 py-3">
            <div className="text-2xs font-medium text-muted-foreground">
              Projected take-home
            </div>
            <div className="text-2xl font-semibold tabular-nums">
              {formatMoney(takeHome)}
            </div>
            <div className="text-xs text-muted-foreground">
              Withheld {formatMoney(withheld)} · {" "}
              {remaining > 0 ? (
                <span className="text-amber-300">
                  still owe {formatMoney(remaining)}
                </span>
              ) : (
                <span className="text-emerald-300">
                  refund {formatMoney(Math.abs(remaining))}
                </span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function BlockedHero({
  missing,
}: {
  missing: { fieldKey: string; label: string; routeHint: string | null; why: string }[];
}) {
  const top = missing.filter((m) => m.label).slice(0, 3);
  return (
    <Card className="border-amber-500/30 bg-amber-500/5">
      <CardContent className="p-8">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="min-w-0 max-w-2xl">
            <div className="text-2xs font-medium text-amber-300">
              Not projecting yet
            </div>
            <h2 className="mt-2 text-3xl font-bold tracking-tight">
              We won&apos;t guess your tax bill.
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Add the {top.length} item{top.length === 1 ? "" : "s"} below and the
              entire dashboard — projection, top moves, Sankey, advisor — comes alive
              with real numbers.
            </p>
            <ul className="mt-4 space-y-2">
              {top.map((m) => (
                <li
                  key={m.fieldKey}
                  className="flex items-start gap-3 rounded-md border border-border bg-surface/50 p-3"
                >
                  <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{m.label}</div>
                    <div className="text-xs text-muted-foreground">{m.why}</div>
                  </div>
                  <Link to={m.routeHint ?? "/tax-intake"}>
                    <Button size="sm" variant="outline">
                      Add
                    </Button>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// NEXT MOVES — top 3 ranked plays
// =============================================================================

function NextMoves({
  moves,
  totalPotential,
  loading,
  blocked,
}: {
  moves: TaxPlay[];
  totalPotential: number;
  loading: boolean;
  blocked: boolean;
}) {
  return (
    <section>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-2xs font-medium text-muted-foreground">
            <Lightbulb className="h-3.5 w-3.5" />
            Next moves
          </div>
          <h2 className="mt-1 text-xl font-semibold tracking-tight">
            {blocked
              ? "Plays will rank by dollar impact once your projection runs"
              : totalPotential > 0
                ? <>You&apos;re leaving <span className="font-bold">{formatMoney(totalPotential)}</span> on the table this year</>
                : "No quantified moves yet — add more business activity to surface plays"}
          </h2>
        </div>
        <Link to="/strategy/tax-plays">
          <Button variant="outline" size="sm">
            See all plays
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-44 w-full" />
          ))}
        </div>
      ) : moves.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
            <Calculator className="h-6 w-6 text-muted-foreground" />
            <p className="max-w-sm text-sm text-muted-foreground">
              {blocked
                ? "Complete the items above and 30+ tax plays will rank themselves by your real marginal rate."
                : "Add business income or mark your eligibility (HSA / spouse / kids) and plays start surfacing."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {moves.map((m, idx) => (
            <PlayCard key={m.key} play={m} rank={idx + 1} />
          ))}
        </div>
      )}
    </section>
  );
}

function PlayCard({ play, rank }: { play: TaxPlay; rank: number }) {
  const route = play.actionRoute.split("#")[0] || "/";
  const hash = play.actionRoute.includes("#")
    ? play.actionRoute.split("#")[1]
    : undefined;

  return (
    <Card className="group flex h-full flex-col transition-shadow hover:shadow-card-hover">
      <CardContent className="flex h-full flex-col gap-4 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-surface text-[11px] font-bold text-foreground">
              {rank}
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {play.category}
            </span>
          </div>
          {play.estimatedDollarImpact != null && (
            <div className="text-right">
              <div className="text-2xl font-bold tabular-nums text-emerald-300">
                {formatMoney(play.estimatedDollarImpact)}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                yr-1 saved
              </div>
            </div>
          )}
        </div>

        <h3 className="text-base font-semibold leading-snug">{play.title}</h3>
        <p className="line-clamp-3 text-sm text-muted-foreground">{play.summary}</p>

        <div className="mt-auto flex items-center justify-between gap-2 pt-2">
          <Link to={route} hash={hash}>
            <Button size="sm" variant="primary">
              {play.actionLabel}
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </Link>
          {play.authorityUrl && (
            <a
              href={play.authorityUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
              title="IRS / authoritative source"
            >
              source <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Right-rail cards
// =============================================================================

function PostureRail({
  entity,
  treatment,
  filing,
  state,
  kids,
  hsaEligible,
}: {
  entity?: string;
  treatment?: string;
  filing?: string;
  state?: string | null;
  kids: number;
  hsaEligible?: boolean;
}) {
  const items = [
    { label: "Entity", value: entity ? humanEntity(entity) : "—" },
    { label: "Tax", value: treatment ? humanTreatment(treatment) : "—" },
    { label: "Filing", value: filing ? humanFiling(filing) : "—" },
    { label: "State", value: state ?? "—" },
    { label: "Kids", value: String(kids) },
    { label: "HSA", value: hsaEligible ? "Eligible" : "—" },
  ];
  return (
    <Card>
      <CardContent className="p-5">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-2xs font-medium text-muted-foreground">
            Posture
          </div>
          <Link to="/people">
            <Button variant="ghost" size="sm" className="-mr-2 h-7 px-2 text-xs">
              Edit
              <ArrowRight className="h-3 w-3" />
            </Button>
          </Link>
        </div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          {items.map((it) => (
            <div key={it.label} className="flex flex-col">
              <dt className="text-2xs text-muted-foreground">
                {it.label}
              </dt>
              <dd className="font-medium">{it.value}</dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}

function CashPlanRail({
  taxReserve,
  solo401k,
  sweepPct,
  assumptionsRoute,
  loading,
}: {
  taxReserve?: number | null;
  solo401k?: number | null;
  sweepPct?: number | null;
  assumptionsRoute: string | null;
  loading: boolean;
}) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-2xs font-medium text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <Wallet className="h-3 w-3" /> Cash plan
            </span>
          </div>
          <Link to="/strategy/cash">
            <Button variant="ghost" size="sm" className="-mr-2 h-7 px-2 text-xs">
              Open
              <ArrowRight className="h-3 w-3" />
            </Button>
          </Link>
        </div>
        {loading ? (
          <Skeleton className="h-16 w-full" />
        ) : taxReserve == null ? (
          <div className="text-sm text-muted-foreground">
            Set a contract rate to project the year.{" "}
            {assumptionsRoute && (
              <Link to={assumptionsRoute} className="text-primary hover:underline">
                Add rate →
              </Link>
            )}
          </div>
        ) : (
          <dl className="space-y-2 text-sm">
            <div className="flex items-baseline justify-between">
              <dt className="text-muted-foreground">Tax reserve</dt>
              <dd className="font-semibold tabular-nums">{formatMoney(taxReserve)}</dd>
            </div>
            <div className="flex items-baseline justify-between">
              <dt className="text-muted-foreground">Solo 401(k)</dt>
              <dd className="font-semibold tabular-nums">{formatMoney(solo401k)}</dd>
            </div>
            <div className="flex items-baseline justify-between">
              <dt className="text-muted-foreground">Sweep %</dt>
              <dd className="font-semibold tabular-nums">{formatPercent(sweepPct, 0)}</dd>
            </div>
          </dl>
        )}
      </CardContent>
    </Card>
  );
}

function SetupRail({ done, total, pct }: { done: number; total: number; pct: number }) {
  if (total === 0) return null;
  return (
    <Card>
      <CardContent className="p-5">
        <div className="mb-2 flex items-center justify-between">
          <div className="text-2xs font-medium text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <ClipboardList className="h-3 w-3" /> Setup
            </span>
          </div>
          <Link to="/setup">
            <Button variant="ghost" size="sm" className="-mr-2 h-7 px-2 text-xs">
              Open
              <ArrowRight className="h-3 w-3" />
            </Button>
          </Link>
        </div>
        <div className="flex items-baseline justify-between">
          <div className="text-lg font-semibold tabular-nums">
            {done} <span className="text-sm text-muted-foreground">/ {total}</span>
          </div>
          {pct === 100 && (
            <Badge tone="success" className="gap-1">
              <CheckCircle2 className="h-3 w-3" /> Done
            </Badge>
          )}
        </div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full bg-primary transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Slim banners
// =============================================================================

function FirstName({ name }: { name?: string }) {
  const first = name?.split(" ")[0];
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <div className="text-2xs font-medium text-muted-foreground">
          Welcome back
        </div>
        <h1 className="mt-1 text-lg font-semibold tracking-tight">
          Hey{first ? `, ${first}` : ""}.
        </h1>
      </div>
      <div className="flex items-center gap-2">
        <Link to="/strategy">
          <Button variant="outline" size="sm">
            <Compass className="h-3.5 w-3.5" />
            Strategy Hub
          </Button>
        </Link>
        <Link to="/strategy/tax-plays">
          <Button size="sm">
            <Sparkles className="h-3.5 w-3.5" />
            Tax Plays
          </Button>
        </Link>
      </div>
    </div>
  );
}

function CriticalBanner({
  card,
}: {
  card: { ruleKey: string; title: string; body: string; primaryActionRoute?: string | null; primaryActionLabel?: string | null; daysUntilDeadline?: number | null };
}) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/10 p-4">
      <Bell className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold">{card.title}</span>
          {card.daysUntilDeadline != null && (
            <Badge tone="warning">{card.daysUntilDeadline}d remaining</Badge>
          )}
        </div>
        <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{card.body}</p>
      </div>
      {card.primaryActionRoute && (
        <Link to={card.primaryActionRoute}>
          <Button size="sm" variant="destructive">
            {card.primaryActionLabel ?? "Open"}
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </Link>
      )}
    </div>
  );
}

// =============================================================================
// Tiny helpers (keep humanizers local — no need for a shared module yet)
// =============================================================================

function humanFiling(s: string) {
  switch (s) {
    case "MarriedFilingJointly": return "MFJ";
    case "MarriedFilingSeparately": return "MFS";
    case "HeadOfHousehold": return "HoH";
    case "QualifyingSurvivingSpouse": return "QSS";
    default: return "Single";
  }
}
function humanEntity(s: string) {
  switch (s) {
    case "SoleProprietor": return "Sole prop";
    case "SingleMemberLLC": return "SMLLC";
    case "MultiMemberLLC": return "Multi-LLC";
    case "SCorp": return "S-Corp";
    case "CCorp": return "C-Corp";
    case "Partnership": return "Partnership";
    default: return s;
  }
}
function humanTreatment(s: string) {
  switch (s) {
    case "DisregardedScheduleC": return "Sch C";
    case "SCorp": return "S-Corp (1120-S)";
    case "CCorp": return "C-Corp (1120)";
    case "Partnership": return "Partnership (1065)";
    default: return s;
  }
}
